import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'
import cn from 'classnames'

import { INPUT_DEBOUNCE_TIME } from 'config/app'
import { Input, Spinner } from 'components'
import { useOutsideClick } from 'hooks'
import { strPartsHighlight } from 'utils/helpers'
import { SuggestFieldItemType } from './components/SuggestField/SuggestField'
import { SuggestField } from './components'
import style from './Suggest.module.css'

export type SuggestItemPropType = {
    id: number
    value: string
}

export type SuggestPropType = {
    classesField?: string
    items: SuggestItemPropType[]
    placeholder?: string
    onSearch: (value: string) => void
    onSelect: (value: SuggestItemPropType) => void
}

const Suggest: React.FC<SuggestPropType> = ({
    classesField,
    items,
    placeholder,
    onSearch,
    onSelect,
}) => {
    const { t } = useTranslation()
    const debounce = useDebouncedCallback(changeCallback, INPUT_DEBOUNCE_TIME)
    const wrapperRef = useRef(null)

    const [query, setQuery] = useState('')
    const [selected, setSelected] = useState('')

    const [isLoading, setIsLoading] = useState(true)
    const [isShowOptions, setIsShowOptions] = useState(false)

    const suggestions = useMemo(() => getSuggestions(items), [items, query])

    const handlerFocus = () => {
        if (suggestions?.length) {
            setIsShowOptions(true)
        }
    }

    const handlerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.currentTarget

        setIsShowOptions(!!value)
        setSelected('')
        setQuery(value)

        debounce()
    }

    const handlerSelect = (id: number) => {
        const selectedItem = items.find((item) => item.id === id)

        setIsShowOptions(false)

        if (selectedItem) {
            setSelected(selectedItem.value)
            onSelect(selectedItem)
        }
    }

    function changeCallback() {
        setIsLoading(true)
        onSearch(query)
    }

    function getSuggestions(suggestionItems: SuggestItemPropType[] = []) {
        const suggestionsParts = suggestionItems.reduce((acc: SuggestFieldItemType[], item: SuggestItemPropType)
            :SuggestFieldItemType [] => {
            const parts = strPartsHighlight(item.value, query)
            return [...acc, { id: item.id, parts }]
        }, [])

        return suggestionsParts.filter((item) => item.parts.length > 1)
    }

    useOutsideClick(wrapperRef, () => {
        setIsShowOptions(false)
    })

    useEffect(() => {
        setIsLoading(false)
    }, [items])

    return (
        <div className={style.wrap} ref={wrapperRef}>
            <Input
                classes={classesField}
                placeholder={placeholder}
                value={selected || query}
                onFocus={handlerFocus}
                onChange={handlerChange}
            />
            <div className={cn(style.options, { [style.options_active]: isShowOptions })}>
                {!suggestions.length ? (
                    <div className={style.noData}>
                        {t('nothing_found')}
                    </div>
                ) : (
                    <>
                        {isLoading && (
                            <Spinner size="small" />
                        )}

                        <ul className={style.list}>
                            {suggestions.map((item) => (
                                <SuggestField
                                    suggest={item}
                                    key={item.id}
                                    onSelect={handlerSelect}
                                />
                            ))}
                        </ul>
                    </>
                )}
            </div>
        </div>
    )
}

export default Suggest
